import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Button, CircularProgress, Alert, IconButton, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Visibility, Send, Refresh } from '@mui/icons-material';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const API = '/api';
const SC = {draft:'#FF9800',submitted:'#1B7A3E',forwarded_erp:'#1565C0',in_progress:'#9C27B0',completed:'#4CAF50'};

export default function SolarRGFList() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [rgfs, setRgfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [viewRgf, setViewRgf] = useState(null);
  const isAdmin = ['admin','superadmin'].includes(user?.role);
  const hdr = { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } };

  const load = async () => {
    try {
      setLoading(true);
      const r = await axios.get(API+'/solar-rgf', hdr);
      setRgfs(r.data.rgfs || []);
    } catch(e) { setError('Failed to load forms'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const forwardToERP = async (id) => {
    if (!window.confirm('Forward this form to ERP & Installation team?')) return;
    try {
      await axios.put(API+'/solar-rgf/'+id+'/forward-erp', {}, hdr);
      setSuccess('Forwarded to ERP successfully');
      setTimeout(()=>setSuccess(''),3000);
      load();
    } catch(e) { setError('Failed to forward'); }
  };

  const leadName = (rgf) => rgf.customerName || (rgf.lead?.name) ||
    ((rgf.lead?.firstName||'')+' '+(rgf.lead?.lastName||'')).trim() || '—';

  return (
    <Box sx={{p:3}}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#003E6F">Solar RGF Submissions</Typography>
          <Typography variant="body2" color="text.secondary">Customer requirement gathering forms submitted by salespersons</Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="outlined" startIcon={<Refresh/>} onClick={load} sx={{borderRadius:2}}>Refresh</Button>
          <Button variant="contained" onClick={()=>navigate('/solar-rgf')}
            sx={{bgcolor:'#E8622A',borderRadius:2}}>+ New Form</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{mb:2}} onClose={()=>setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{mb:2}} onClose={()=>setSuccess('')}>{success}</Alert>}

      {loading ? <Box textAlign="center" py={6}><CircularProgress/></Box> : (
        <Card sx={{borderRadius:2,boxShadow:'0 2px 12px rgba(0,62,111,0.08)'}}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{'& th':{bgcolor:'#003E6F',color:'white',fontWeight:700,fontSize:12,py:1.5}}}>
                  <TableCell>#</TableCell>
                  <TableCell>Form No</TableCell>
                  <TableCell>Customer Name</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>System Type</TableCell>
                  <TableCell>Capacity (kWp)</TableCell>
                  <TableCell>Salesperson</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rgfs.length === 0 ? (
                  <TableRow><TableCell colSpan={11} align="center" sx={{py:4,color:'#999'}}>No forms submitted yet</TableCell></TableRow>
                ) : rgfs.map((rgf,i) => (
                  <TableRow key={rgf._id} sx={{'&:nth-of-type(odd)':{bgcolor:'#F7FAFD'}}}>
                    <TableCell sx={{fontSize:12}}>{i+1}</TableCell>
                    <TableCell sx={{fontSize:12,fontWeight:600,color:'#003E6F'}}>{rgf.formNo||'—'}</TableCell>
                    <TableCell sx={{fontSize:12,fontWeight:500}}>{leadName(rgf)}</TableCell>
                    <TableCell sx={{fontSize:12}}>{rgf.lead?.phone||'—'}</TableCell>
                    <TableCell sx={{fontSize:12}}>{rgf.district||rgf.lead?.city||'—'}</TableCell>
                    <TableCell sx={{fontSize:11}}>{rgf.systemType||'—'}</TableCell>
                    <TableCell sx={{fontSize:12}}>{rgf.proposedCapacity||'—'}</TableCell>
                    <TableCell sx={{fontSize:12}}>{rgf.salesperson?.name||'—'}</TableCell>
                    <TableCell>
                      <Chip label={(rgf.status||'draft').replace('_',' ')} size="small"
                        sx={{bgcolor:(SC[rgf.status]||'#999')+'20',color:SC[rgf.status]||'#999',fontWeight:700,fontSize:10,height:20}}/>
                    </TableCell>
                    <TableCell sx={{fontSize:11,color:'#666'}}>
                      {rgf.createdAt ? new Date(rgf.createdAt).toLocaleDateString('en-IN') : '—'}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        <IconButton size="small" onClick={()=>setViewRgf(rgf)} sx={{color:'#1565C0'}}>
                          <Visibility sx={{fontSize:16}}/>
                        </IconButton>
                        <IconButton size="small" onClick={()=>navigate('/solar-rgf?id='+rgf._id)} sx={{color:'#E8622A'}}>
                          <Send sx={{fontSize:16}}/>
                        </IconButton>
                        {isAdmin && rgf.status==='submitted' && (
                          <Button size="small" variant="contained" onClick={()=>forwardToERP(rgf._id)}
                            sx={{bgcolor:'#1565C0',fontSize:10,py:0.3,borderRadius:1,minWidth:0,px:1}}>
                            ERP
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* View Dialog */}
      <Dialog open={!!viewRgf} onClose={()=>setViewRgf(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{bgcolor:'#003E6F',color:'white',fontWeight:700}}>
          Solar RGF — {viewRgf?.formNo} — {viewRgf && leadName(viewRgf)}
        </DialogTitle>
        <DialogContent sx={{p:3}}>
          {viewRgf && (
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} fontSize={13}>
              {[
                ['Form No', viewRgf.formNo],['Status', viewRgf.status],
                ['Customer', leadName(viewRgf)],['Phone', viewRgf.lead?.phone],
                ['System Type', viewRgf.systemType],['Capacity', viewRgf.proposedCapacity+' kWp'],
                ['Panel Tech', viewRgf.panelTechnology],['Inverter', viewRgf.inverterType],
                ['Total Cost', '₹'+viewRgf.totalCost],['Subsidy', viewRgf.subsidy],
                ['Payment Mode', viewRgf.paymentMode],['Down Payment', '₹'+viewRgf.downPayment],
                ['Rooftop Type', viewRgf.rooftopType],['Rooftop Area', viewRgf.availableRooftopArea+' sq ft'],
                ['Salesperson', viewRgf.salesperson?.name],['Submitted', viewRgf.submittedAt ? new Date(viewRgf.submittedAt).toLocaleDateString('en-IN') : '—'],
              ].map(([label,val])=>(
                <Box key={label} sx={{p:1.5,bgcolor:'#F7FAFD',borderRadius:1,border:'1px solid #E2E8F0'}}>
                  <Typography fontSize={11} color="text.secondary">{label}</Typography>
                  <Typography fontSize={13} fontWeight={600}>{val||'—'}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setViewRgf(null)}>Close</Button>
          {isAdmin && viewRgf?.status==='submitted' && (
            <Button variant="contained" onClick={()=>{forwardToERP(viewRgf._id);setViewRgf(null);}}
              sx={{bgcolor:'#1565C0'}}>Forward to ERP</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}